import { ref } from 'vue';
import { httpService, type ApiResponse } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import { compressImage } from '../utils/compressImage';
import '../contracts/Products_SalvarInput.ts';
import '../contracts/ProductCategories_SalvarInput.ts';
import '../contracts/Products_UploadImageInput';
import '../contracts/Products_DeleteImageInput';
import '../contracts/Products_RemoverInput';
import '../contracts/Products_ReorderImagesInput';

import '../contracts/Products_GetResponse';
import '../contracts/Products_RemoverResponse';
import '../contracts/Products_UploadImageResponse';
import '../contracts/Products_ListImagesResponse';
import '../contracts/Products_ListImageRow';
import '../contracts/Products_ReorderImagesResponse';
import '../contracts/Products_SalvarResponse';
import '../contracts/Products_DeleteImageResponse';
import '../contracts/Products_SetPrimaryImageResponse';
import '../contracts/Products_SetPrimaryImageInput';
import '../contracts/ProductCategories_KvResponse';
import type { Products_SalvarInput } from '../contracts/Products_SalvarInput';
import type { Products_UploadImageResponseDados } from '../contracts/Products_UploadImageResponse';
import type { Products_ListImageRow } from '../contracts/Products_ListImageRow';
import type { Products_ReorderImagesInput } from '../contracts/Products_ReorderImagesInput';
import type { Products_GetResponseDados } from '../contracts/Products_GetResponse';
import type { ProductCategories_KvOptionRow } from '../contracts/ProductCategories_KvOptionRow';

/**
 * Composable for SO Product management operations.
 * Manages product CRUD, categories, and image gallery operations.
 */
export function useSOProducts() {
  const isLoading = ref(false);
  const error = ref(null);

  const saveProduct = async (payload: Products_SalvarInput) => {
    log('useSOProducts', 'saveProduct', 'Saving product', payload);
    isLoading.value = true;
    error.value = null;
    try {
      validate('Products_SalvarInput', payload);
      const response = await httpService.post<ApiResponse>('/index.php/so/products/salvar', payload, { responseContract: 'Products_SalvarResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao salvar produto');
    } catch (err) {
      logError('useSOProducts', 'saveProduct', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchProductCategories = async () => {
    log('useSOProducts', 'fetchProductCategories', 'Fetching product categories');
    try {
      return await httpService.get<ApiResponse<ProductCategories_KvOptionRow[]>>('/index.php/so/product_categories/kv', { responseContract: 'ProductCategories_KvResponse' });
    } catch (err) {
      logError('useSOProducts', 'fetchProductCategories', 'Error', err);
      throw err;
    }
  };

  const saveProductCategory = async (payload: Record<string, unknown>) => {
    log('useSOProducts', 'saveProductCategory', 'Saving product category', payload);
    try {
      validate('ProductCategories_SalvarInput', payload);
      return await httpService.post<ApiResponse>('/index.php/so/product_categories/salvar', payload, { responseContract: 'ProductCategories_SalvarResponse' });
    } catch (err) {
      logError('useSOProducts', 'saveProductCategory', 'Error', err);
      throw err;
    }
  };

  const getProduct = async (productId: number): Promise<Products_GetResponseDados> => {
    log('useSOProducts', 'getProduct', 'Fetching product', { productId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get<ApiResponse<Products_GetResponseDados>>(
        `/index.php/so/products/get/${productId}`,
        { responseContract: 'Products_GetResponse' }
      );
      if (response.success) {
        return response.dados as Products_GetResponseDados;
      }
      throw new Error(response.error || 'Erro ao buscar produto');
    } catch (err) {
      logError('useSOProducts', 'getProduct', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Upload a single image to a product's gallery. Compresses the file
   * client-side (1920px max, 80% JPEG quality) before sending a multipart
   * POST. The Input contract is registered for the QA rule's sake; FormData
   * bodies skip runtime validation in httpService.request().
   */
  const uploadProductImage = async (
    productId: number,
    file: File,
    options?: { altText?: string; sortOrder?: number; isPrimary?: boolean }
  ): Promise<Products_UploadImageResponseDados | undefined> => {
    log('useSOProducts', 'uploadProductImage', 'Uploading product image', { productId, fileName: file.name });
    isLoading.value = true;
    error.value = null;
    try {
      const compressed = await compressImage(file);
      const formData = new FormData();
      formData.append('imagem_nome', compressed);
      formData.append('so_product_id', String(productId));
      if (options?.altText !== undefined) {
        formData.append('so_product_image_alt_text', options.altText);
      }
      if (options?.sortOrder !== undefined) {
        formData.append('so_product_image_sort_order', String(options.sortOrder));
      }
      if (options?.isPrimary !== undefined) {
        formData.append('so_product_image_is_primary', options.isPrimary ? '1' : '0');
      }
      const response = await httpService.post<ApiResponse<Products_UploadImageResponseDados>>(
        '/index.php/so/products/uploadImage',
        formData,
        { responseContract: 'Products_UploadImageResponse', inputContract: 'Products_UploadImageInput' }
      );
      if (response.success) {
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao enviar imagem do produto');
    } catch (err) {
      logError('useSOProducts', 'uploadProductImage', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchProductImages = async (productId: number): Promise<Products_ListImageRow[]> => {
    log('useSOProducts', 'fetchProductImages', 'Fetching product images', { productId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get<ApiResponse<Products_ListImageRow[]>>(
        `/index.php/so/products/listImages/${productId}`,
        { responseContract: 'Products_ListImagesResponse' }
      );
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar imagens do produto');
    } catch (err) {
      logError('useSOProducts', 'fetchProductImages', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const deleteProductImage = async (imageId: number) => {
    log('useSOProducts', 'deleteProductImage', 'Deleting product image', { imageId });
    isLoading.value = true;
    error.value = null;
    try {
      const payload = { so_product_image_id: imageId };
      const response = await httpService.post<ApiResponse>(
        '/index.php/so/products/deleteImage',
        payload,
        { responseContract: 'Products_DeleteImageResponse', inputContract: 'Products_DeleteImageInput' }
      );
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao remover imagem do produto');
    } catch (err) {
      logError('useSOProducts', 'deleteProductImage', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const setPrimaryProductImage = async (imageId: number) => {
    log('useSOProducts', 'setPrimaryProductImage', 'Setting primary product image', { imageId });
    isLoading.value = true;
    error.value = null;
    try {
      const payload = { so_product_image_id: imageId };
      const response = await httpService.post<ApiResponse>(
        '/index.php/so/products/setPrimaryImage',
        payload,
        { responseContract: 'Products_SetPrimaryImageResponse', inputContract: 'Products_SetPrimaryImageInput' }
      );
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao definir imagem principal');
    } catch (err) {
      logError('useSOProducts', 'setPrimaryProductImage', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Reorder a product's image gallery in one transaction. `orderedImageIds`
   * is the new ordering — index 0 becomes sort_order=1, index 1 becomes 2,
   * and so on. The response only confirms the product id; callers should
   * follow up with `fetchProductImages` to read back the new sort_order
   * values from the database.
   */
  const reorderProductImages = async (productId: number, orderedImageIds: number[]) => {
    log('useSOProducts', 'reorderProductImages', 'Reordering product images', { productId, orderedImageIds });
    isLoading.value = true;
    error.value = null;
    try {
      const payload: Products_ReorderImagesInput = {
        so_product_id: productId,
        ordered_image_ids: orderedImageIds
      };
      validate('Products_ReorderImagesInput', payload);
      const response = await httpService.post<ApiResponse>(
        '/index.php/so/products/reorderImages',
        payload,
        { responseContract: 'Products_ReorderImagesResponse', inputContract: 'Products_ReorderImagesInput' }
      );
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao reordenar imagens do produto');
    } catch (err) {
      logError('useSOProducts', 'reorderProductImages', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const removeProduct = async (productId: number) => {
    log('useSOProducts', 'removeProduct', 'Removing product', { productId });
    isLoading.value = true;
    error.value = null;
    try {
      const payload = { so_product_id: productId };
      const response = await httpService.post<ApiResponse>(
        '/index.php/so/products/remover',
        payload,
        { responseContract: 'Products_RemoverResponse', inputContract: 'Products_RemoverInput' }
      );
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao remover produto');
    } catch (err) {
      logError('useSOProducts', 'removeProduct', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    saveProduct,
    fetchProductCategories,
    saveProductCategory,
    getProduct,
    removeProduct,
    uploadProductImage,
    fetchProductImages,
    deleteProductImage,
    setPrimaryProductImage,
    reorderProductImages
  };
}
